import {Injectable, isDevMode} from '@angular/core';
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {AdmissionCycle, AdmissionLog, FullChild, TransferRecord} from "../interfaces/FullChild";
import {Timestamp} from "firebase/firestore";
import firebase from "firebase/compat/app";
import {Observable} from "rxjs";
import {map, tap} from "rxjs/operators";
import {environment} from "../../environments/environment";
export interface AdmissionCounter{
  totalLogs:number
  lastUpdated:Date|Timestamp
}
@Injectable({
  providedIn: 'root'
})
// src/app/services/admission-log.service.ts
export class AdmissionLogService {
  count: number = 0;
  readonly env = isDevMode() ? environment.testMode : environment.productionMode;

  constructor(private readonly mFirestore: AngularFirestore) {
    this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').get().toPromise()
        .then(doc => {
          if (doc && doc.exists) {
            const data = doc.data() as any;
            this.count = data?.totalLogs ?? 0;
          }
        })
        .catch(err => {
          console.error('Failed to read admissionCounts:', err);
        });

  }
  getAdmissionCounter(){
  return   this.mFirestore.collection(this.env.dashboard).doc<AdmissionCounter>('admissionCounts').get().toPromise()
  }

  async CreateAdmissionLogs() {
    // Always read fresh count from Firestore to avoid stale this.count from constructor
    const counterSnap = await this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').get().toPromise();
    if (counterSnap && counterSnap.exists) {
      this.count = (counterSnap.data() as any)?.totalLogs ?? 0;
    }

    const snapshot = await this.mFirestore.collection<FullChild>(this.env.child, ref => ref.orderBy('childCode')).get().toPromise();
    const children = snapshot?.docs.map(doc => doc.data()) || [];
    let allLogs: AdmissionLog[] = [];
    let skippedChildren = 0;

    children.forEach(child => {
      try {
        console.log('processing child:', child.childCode);
        allLogs.push(...this.getAdmissionLogsFromChild(child));
      } catch (e) {
        skippedChildren++;
        console.error(`Failed to parse logs for child ${child.childCode}, skipping:`, e);
      }
    });

    if (skippedChildren > 0) {
      console.warn(`Skipped ${skippedChildren} children due to errors.`);
    }

    console.log(`Total logs to add: ${allLogs.length}`);
    await this.pushAdmissionLogsBatch(allLogs);
  }

  getAdmissionLogsFromChild(child: FullChild): AdmissionLog[] {
    const logs: AdmissionLog[] = [];

    // Guard: skip children with no admission history
    if (!child.admissionData || child.admissionData.length === 0) {
      console.warn(`Child ${child.childCode} has no admissionData, skipping.`);
      return logs;
    }

    child.admissionData.forEach((admission, i: number) => {
      const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
      const guardianName = (idx: number) => {
        const g = getGuardian(idx) as any;
        if (!g) return 'NA';
        const name = `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim();
        return name || 'NA';
      };
      const guardianField = (idx: number, field: string) => {
        const g = getGuardian(idx) as any;
        return g?.[field] ?? 'NA';
      };

      // Fix: use correct casing matching the AdmissionLog interface
      const resolvedType = ((): AdmissionLog['type'] => {
        if (i === 0) return 'Admission';
        if (i === 1) return 'Discharge';
        if (i % 2 === 0) return 'FollowUpAdmission';
        return 'FollowUpDischarge';
      })();

      const baseLog = {
        centreName: admission.centreName || child.centreName,
        childCode: child.childCode,
        childName: `${child.firstName ?? ''} ${child.lastName ?? ''}`.trim(),
        currentUnit: admission.currentUnit || child.selectUnit || 'NA',
        dob: child.dob?.toDate?.() ?? new Date(0),
        gender: child.gender,
        hospital: child.hospital,
        isUnique: i === 0 || i === 1,
        type: resolvedType,
        centreCode: admission.centreCode || child.centreCode,
        bloodGroup: child.bloodGroup || 'NA',
        diagnosis: child.diagnosisSelected,
        city: child.city || 'NA',
        state: child.state,
        age: getAge(child.dob),
        guardian1Name: guardianName(0),
        guardian2Name: guardianName(1),
        guardian1Occupation: guardianField(0, 'occupation'),
        guardian2Occupation: guardianField(1, 'occupation'),
        guardian1Relation: guardianField(0, 'relation'),
        guardian2Relation: guardianField(1, 'relation'),
        createdAt: new Date(),
      };

      if (i % 2 === 0) {
        // Admission or FollowUpAdmission
        const admTimestamp = admission.dateOfAdmission as unknown as Timestamp;
        if (!admTimestamp) {
          console.warn(`Child ${child.childCode} admission[${i}] missing dateOfAdmission, skipping entry.`);
          return;
        }
        logs.push({
          ...baseLog,
          dateOfAdmission: admTimestamp.toDate(),
        });
      } else {
        // Discharge or FollowUpDischarge
        const disTimestamp = admission.dateOfDischarge as unknown as Timestamp;
        if (!disTimestamp) {
          console.warn(`Child ${child.childCode} admission[${i}] missing dateOfDischarge, skipping entry.`);
          return;
        }
        logs.push({
          ...baseLog,
          dateOfDischarge: disTimestamp.toDate(),
        });
      }
    });

    return logs;
  }

  private chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }


  // typescript
  async pushAdmissionLogsBatch(logs: AdmissionLog[], chunkSize = 450) {
    if (!logs || logs.length === 0) {
      console.log('No logs to push.');
      return;
    }

    const chunks = this.chunkArray(logs, chunkSize);
    const lastLogRef = this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').ref;
    const lastLogAddedDate = new Date();

    // Use a local baseCount so admissionCount for each log is sequential and we don't
    // mutate this.count until all chunks commit successfully.
    let baseCount = this.count;

    for (const chunk of chunks) {
      const batch = this.mFirestore.firestore.batch();

      for (const log of chunk) {
        const docRef = this.mFirestore.collection(this.env.admissionLogHistory).doc().ref;
        const currentCount = ++baseCount; // increment per log and include in logWithId
        const logWithId = {
          ...log,
          admissionId: docRef.id,
          admissionCount: currentCount,
          createdAt: (log as any).createdAt ?? new Date(),
          createdBy: 'System',
        };

        try {
          batch.set(docRef, logWithId);
        } catch (e) {
          console.error('Error queueing log for childCode:', (log as any).childCode, e);
          throw e;
        }
      }

      // Update dashboard counter for this chunk
      batch.set(
          lastLogRef,
          {
            lastUpdated: lastLogAddedDate,
            totalLogs: firebase.firestore.FieldValue.increment(chunk.length),
          },
          {merge: true}
      );

      try {
        await batch.commit();
        console.log(`Committed chunk of ${chunk.length} logs.`);
      } catch (e) {
        console.error('Batch commit failed for chunk:', e);
        throw e;
      }
    }

    // All chunks committed successfully — update service count
    this.count = baseCount;
    alert('Admission Logs added successfully!! Total document counts are ' + this.count);
    console.log(`Total count: ${this.count}`);
  }


  fetchAdmissionLogs() {
    return this.mFirestore.collection(this.env.admissionLogHistory).get().toPromise();
  }

  getRecentAdmissions(): Observable<any[]> {
    return this.mFirestore
      .collection(this.env.admissionLogHistory, ref =>
        ref
          .where('type', '==', 'Admission')
          .orderBy('dateOfAdmission', 'desc')
          .limit(10)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(data => this.makeUniqueByChild(data))
      );
  }

  getRecentDischarges(): Observable<any[]> {
    return this.mFirestore
      .collection(this.env.admissionLogHistory, ref =>
        ref
          .where('type', '==', 'Discharge')
          .orderBy('dateOfDischarge', 'desc')
          .limit(10)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(data => this.makeUniqueByChild(data))
      );
  }

  private makeUniqueByChild(data: any[]): any[] {
    const map = new Map<string, any>();

    data.forEach(item => {
      if (!map.has(item.childCode)) {
        map.set(item.childCode, item);
      }
    });

    return Array.from(map.values()).slice(0, 10);
  }
  getAdmissionChildByChildCode(childCode: string) {
    console.log('getAdmissionChildByChildCode called with:', childCode);

    return this.mFirestore
      .collection<FullChild>(this.env.child, ref =>
        ref.where('childCode', '==', childCode).limit(1)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        tap(res => console.log('Admission Child RES:', res))
      );
  }

  getDischargeChildByChildCode(childCode: string) {
    console.log('getDischargeChildByChildCode called with:', childCode);

    return this.mFirestore
      .collection<FullChild>(this.env.child, ref =>
        ref.where('childCode', '==', childCode).limit(1)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        tap(res => console.log('Discharge Child RES:', res))
      );
  }





  async updateCycleOnTransfer(
    lastDocId: string,
    transfer: TransferRecord,
    updatedLocation?: { centreCode: string; centreName: string; currentUnit: string }
  ): Promise<void> {
    if (!lastDocId) return;

    const update: any = {
      transferred: true,
      transferDate: transfer.date,
      transferFromCentreCode: transfer.fromCentreCode ?? null,
      transferFromCentreName: transfer.fromCentreName ?? null,
      transferFromUnit: transfer.fromUnit ?? null,
      transferToCentreCode: transfer.toCentreCode ?? null,
      transferToCentreName: transfer.toCentreName ?? null,
      transferToUnit: transfer.toUnit ?? null,
      transferBy: transfer.by ?? null,
      transfers: firebase.firestore.FieldValue.arrayUnion(transfer),
      updatedAt: new Date(),
    };

    if (updatedLocation) {
      update.centreCode = updatedLocation.centreCode;
      update.centreName = updatedLocation.centreName;
      update.currentUnit = updatedLocation.currentUnit;
    }

    await this.mFirestore.collection(this.env.admissionCycles).doc(lastDocId).update(update);
  }

  async createCycleOnAdmission(child: any, createdBy: string = 'System'): Promise<void> {
    const childCode: string = child.childCode;
    const docId = `${childCode}-1`;

    const admDate: Date = child.dateOfAdmission?.toDate
      ? child.dateOfAdmission.toDate()
      : new Date(child.dateOfAdmission);

    const cycle: AdmissionCycle = {
      ...this.buildCycleBase(child, createdBy),
      cycleId: docId,
      cycleNumber: 1,
      isFollowUp: false,
      isUnique: true,
      isCurrentlyAdmitted: true,
      centreCode: child.centreCode,
      centreName: child.centreName,
      currentUnit: child.selectUnit || 'NA',
      dateOfAdmission: admDate,
      dateOfDischarge: null,
      firstAdmission: admDate,
      firstDischarge: null,
    };

    const batch = this.mFirestore.firestore.batch();
    batch.set(this.mFirestore.collection(this.env.admissionCycles).doc(docId).ref, stripUndefined(cycle));
    batch.update(this.mFirestore.collection(this.env.child).doc(childCode).ref, { lastDocId: docId });
    await batch.commit();
  }

  async updateCycleOnDischarge(lastDocId: string, dischargeDate: any): Promise<void> {
    if (!lastDocId) return;
    await this.mFirestore.collection(this.env.admissionCycles).doc(lastDocId).update({
      isCurrentlyAdmitted: false,
      dateOfDischarge: dischargeDate,
      updatedAt: new Date(),
    });
  }

  async createCycleOnReAdmission(
    child: FullChild,
    dateOfAdmission: any,
    selectedCentre: { centreCode: string; centreName: string } | null | undefined,
    selectUnit: string,
    createdBy: string = 'System'
  ): Promise<void> {
    const childCode = child.childCode;
    const completedCycles = Math.floor((child.admissionData?.length ?? 0) / 2);
    const cycleNumber = completedCycles + 1;
    const docId = `${childCode}-${cycleNumber}`;

    const admDate: Date = dateOfAdmission?.toDate
      ? dateOfAdmission.toDate()
      : new Date(dateOfAdmission);

    const firstAdmTimestamp = child.admissionData?.[0]?.dateOfAdmission as unknown as Timestamp | null;
    const firstDisTimestamp = (child.admissionData?.[1]?.dateOfDischarge ?? null) as unknown as Timestamp | null;

    const cycle: AdmissionCycle = {
      ...this.buildCycleBase(child, createdBy),
      cycleId: docId,
      cycleNumber,
      isFollowUp: true,
      isUnique: false,
      isCurrentlyAdmitted: true,
      centreCode: selectedCentre?.centreCode ?? child.centreCode,
      centreName: selectedCentre?.centreName ?? child.centreName,
      currentUnit: selectUnit || 'NA',
      dateOfAdmission: admDate,
      dateOfDischarge: null,
      firstAdmission: firstAdmTimestamp ? firstAdmTimestamp.toDate() : new Date(0),
      firstDischarge: firstDisTimestamp ? firstDisTimestamp.toDate() : null,
    };

    const batch = this.mFirestore.firestore.batch();
    batch.set(this.mFirestore.collection(this.env.admissionCycles).doc(docId).ref, stripUndefined(cycle));
    batch.update(this.mFirestore.collection(this.env.child).doc(childCode).ref, { lastDocId: docId });
    await batch.commit();
  }

  private buildCycleBase(child: any, createdBy: string): Omit<AdmissionCycle,
    'cycleId' | 'cycleNumber' | 'isFollowUp' | 'isUnique' | 'isCurrentlyAdmitted' |
    'centreCode' | 'centreName' | 'currentUnit' |
    'dateOfAdmission' | 'dateOfDischarge' | 'firstAdmission' | 'firstDischarge'> {
    const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
    const guardianName = (idx: number) => {
      const g = getGuardian(idx) as any;
      if (!g) return 'NA';
      return `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim() || 'NA';
    };
    const guardianField = (idx: number, field: string) => ((getGuardian(idx) as any)?.[field]) ?? 'NA';

    return {
      childCode: child.childCode,
      childName: `${child.firstName ?? ''} ${child.lastName ?? ''}`.trim(),
      firstName: child.firstName ?? '',
      lastName: child.lastName ?? '',
      dob: child.dob?.toDate?.() ?? new Date(0),
      gender: child.gender,
      age: getAge(child.dob),
      hospital: child.hospital,
      bloodGroup: child.bloodGroup || 'NA',
      diagnosis: child.diagnosisSelected ?? [],
      city: child.city || 'NA',
      state: child.state,
      guardian1Name: guardianName(0),
      guardian2Name: guardianName(1),
      guardian1Occupation: guardianField(0, 'occupation'),
      guardian2Occupation: guardianField(1, 'occupation'),
      guardian1Relation: guardianField(0, 'relation'),
      guardian2Relation: guardianField(1, 'relation'),
      createdAt: new Date(),
      createdBy,
      updatedAt: new Date(),
    };
  }

  async deleteAllAdmissionLogs() {
    const res = await this.mFirestore.collection(this.env.admissionLogHistory).get().toPromise();
    const batch = this.mFirestore.firestore.batch();
    res?.forEach(it => {
      batch.delete(this.mFirestore.collection(this.env.admissionLogHistory).doc(it.id).ref);
    });
    await batch.commit();
    console.log('All AdmissionLogs deleted.');
  }

  getTotalAdmissionLogsCount() {
    return this.mFirestore.collection(this.env.admissionLogHistory).get().toPromise();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ADMISSION CYCLE LOGS
  // One doc per child stay (admission + optional discharge) written to
  // ALAdmissionCycles collection.  ALChild/{childCode}.lastDocId is updated
  // to point to each child's latest cycle document.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Derive AdmissionCycle objects from a single child's admissionData array.
   * Even indices (0, 2, 4 …) are admission entries; odd indices (1, 3, 5 …)
   * are discharge entries.  If no matching discharge exists the cycle is open
   * (isCurrentlyAdmitted = true, dateOfDischarge = null).
   */
  getAdmissionCyclesFromChild(child: FullChild | any): Omit<AdmissionCycle, 'cycleId' | 'createdAt' | 'createdBy'>[] {
    const cycles: Omit<AdmissionCycle, 'cycleId' | 'createdAt' | 'createdBy'>[] = [];

    if (!child.admissionData || child.admissionData.length === 0) {
      console.warn(`Child ${child.childCode} has no admissionData — skipping.`);
      return cycles;
    }

    const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
    const guardianName = (idx: number) => {
      const g = getGuardian(idx) as any;
      if (!g) return 'NA';
      return `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim() || 'NA';
    };
    const guardianField = (idx: number, field: string) => {
      const g = getGuardian(idx) as any;
      return g?.[field] ?? 'NA';
    };

    const admissionEntries = child.admissionData.filter((_: any, i: number) => i % 2 === 0);
    const dischargeEntries = child.admissionData.filter((_: any, i: number) => i % 2 !== 0);

    // Resolve first-ever admission / discharge dates (cycle 1 — index 0)
    const firstAdmTimestamp = admissionEntries[0]?.dateOfAdmission as unknown as Timestamp | null;
    const firstDisTimestamp = (dischargeEntries[0]?.dateOfDischarge ?? null) as unknown as Timestamp | null;
    const firstAdmissionDate: Date = firstAdmTimestamp ? firstAdmTimestamp.toDate() : new Date(0);
    const firstDischargeDate: Date | null = firstDisTimestamp ? firstDisTimestamp.toDate() : null;

    admissionEntries.forEach((admEntry: any, cycleIdx: number) => {
      const disEntry = dischargeEntries[cycleIdx] ?? null;

      const admTimestamp = admEntry?.dateOfAdmission as unknown as Timestamp | null;
      const disTimestamp = disEntry?.dateOfDischarge as unknown as Timestamp | null;

      if (!admTimestamp) {
        console.warn(`Child ${child.childCode} cycle[${cycleIdx + 1}] missing dateOfAdmission, skipping.`);
        return;
      }

      cycles.push({
        childCode: child.childCode,
        childName: `${child.firstName ?? ''} ${child.lastName ?? ''}`.trim(),
        firstName: child.firstName ?? '',
        lastName: child.lastName ?? '',
        cycleNumber: cycleIdx + 1,
        isFollowUp: cycleIdx > 0,
        isUnique: cycleIdx === 0,          // mirrors AdmissionLog.isUnique
        isCurrentlyAdmitted: !disTimestamp,
        // followUpOfAdmissionId omitted — Firestore rejects `undefined`; set explicitly only when linking

        centreCode: admEntry.centreCode || child.centreCode,
        centreName: admEntry.centreName || child.centreName,
        currentUnit: admEntry.currentUnit || child.selectUnit || 'NA',

        dateOfAdmission: admTimestamp.toDate(),
        dateOfDischarge: disTimestamp ? disTimestamp.toDate() : null,

        firstAdmission: firstAdmissionDate,
        firstDischarge: firstDischargeDate,

        dob: child.dob?.toDate?.() ?? new Date(0),
        gender: child.gender,
        age: getAge(child.dob),
        hospital: child.hospital,
        bloodGroup: child.bloodGroup || 'NA',
        diagnosis: child.diagnosisSelected ?? [],
        city: child.city || 'NA',
        state: child.state,

        guardian1Name: guardianName(0),
        guardian2Name: guardianName(1),
        guardian1Occupation: guardianField(0, 'occupation'),
        guardian2Occupation: guardianField(1, 'occupation'),
        guardian1Relation: guardianField(0, 'relation'),
        guardian2Relation: guardianField(1, 'relation'),
      });
    });

    return cycles;
  }

  /**
   * Bulk-create AdmissionCycle documents for ALL children in ALChild collection.
   *
   * For each child:
   *  - n / 2 cycle documents are created (rounded up if last admission has no discharge yet)
   *  - ALChild/{childCode} is updated with `lastDocId` pointing to the latest cycle doc
   *
   * Safe to re-run:  existing ALAdmissionCycles docs are overwritten (set), not duplicated.
   * Writes are chunked at 400 ops/batch to stay well under Firestore's 500-op limit.
   */
  async createAdmissionCycleLogs(createdBy: string = 'System'): Promise<void> {
    const snapshot = await this.mFirestore
      .collection<FullChild>(this.env.child, ref => ref.orderBy('childCode'))
      .get()
      .toPromise();

    const children = snapshot?.docs.map(doc => ({ ...(doc.data() as FullChild), id: doc.id })) || [];
    console.log(`Processing ${children.length} children for admission cycles…`);

    // ── Step 1: Build all cycle write descriptors in memory ─────────────────
    interface CycleWrite {
      docRef: any; // firebase.firestore.DocumentReference
      payload: AdmissionCycle;
      childCode: string;
      isLastForChild: boolean;
    }

    const writes: CycleWrite[] = [];
    let skippedChildren = 0;

    for (const child of children) {
      try {
        const cycles = this.getAdmissionCyclesFromChild(child);
        if (cycles.length === 0) continue;

        cycles.forEach((cycle, idx) => {
          // Deterministic doc ID: childCode-cycleNumber (e.g. "AL001-1", "AL001-2")
          // Re-runs safely overwrite the same document instead of creating duplicates.
          const docId = `${child.childCode}-${cycle.cycleNumber}`;
          const docRef = this.mFirestore.collection(this.env.admissionCycles).doc(docId).ref;
          writes.push({
            docRef,
            payload: {
              ...cycle,
              cycleId: docId,
              createdAt: new Date(),
              createdBy,
              updatedAt: new Date(),
            },
            childCode: child.childCode,
            isLastForChild: idx === cycles.length - 1,
          });
        });
      } catch (e) {
        skippedChildren++;
        console.error(`Failed to parse cycles for child ${child.childCode}:`, e);
      }
    }

    if (skippedChildren > 0) console.warn(`Skipped ${skippedChildren} children due to errors.`);
    console.log(`Total cycle documents to write: ${writes.length}`);

    if (writes.length === 0) {
      alert('No admission cycle documents to create.');
      return;
    }

    // ── Step 2: Build per-child lastDocId map (latest cycle for each child) ──
    const lastDocIdByChild = new Map<string, string>();
    writes.filter(w => w.isLastForChild).forEach(w => {
      lastDocIdByChild.set(w.childCode, w.docRef.id);
    });

    // ── Step 3: Batch write cycles + ALChild lastDocId updates ───────────────
    const CHUNK = 200; // cycle writes + ALChild updates combined, stay under 500
    let totalWritten = 0;

    // Chunk only the cycle writes; append ALChild updates per batch
    for (let i = 0; i < writes.length; i += CHUNK) {
      const chunk = writes.slice(i, i + CHUNK);
      const batch = this.mFirestore.firestore.batch();

      // Write cycle documents — strip undefined fields before writing (Firestore rejects undefined)
      chunk.forEach(w => batch.set(w.docRef, stripUndefined(w.payload)));

      // Update ALChild.lastDocId for children whose last cycle is in this chunk
      const childCodesInChunk = new Set(
        chunk.filter(w => w.isLastForChild).map(w => w.childCode)
      );
      childCodesInChunk.forEach(childCode => {
        const lastDocId = lastDocIdByChild.get(childCode)!;
        const childRef = this.mFirestore.collection(this.env.child).doc(childCode).ref;
        batch.update(childRef, { lastDocId });
      });

      await batch.commit();
      totalWritten += chunk.length;
      console.log(`Committed ${totalWritten} / ${writes.length} cycle docs (${childCodesInChunk.size} ALChild docs updated in this batch).`);
    }

    alert(
      `✅ Admission Cycle Logs created!\n` +
      `Total cycles written: ${writes.length}\n` +
      `Children processed: ${children.length - skippedChildren}\n` +
      `Children skipped: ${skippedChildren}`
    );
    console.log('createAdmissionCycleLogs complete.');
  }

  async createUniqueAdmissionLogObject(child: any, admissionType: string,createdBy:string='System'): Promise<void> {
    let admissionCounterSnapshot  = await  this.getAdmissionCounter()
const batch = this.mFirestore.firestore.batch();
    const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
    const guardianName = (idx: number) => {
      const g = getGuardian(idx) as any;
      if (!g) return 'NA';
      const name = `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim();
      return name || 'NA';
    };
    const guardianField = (idx: number, field: string) => {
      const g = getGuardian(idx) as any;
      return g?.[field] ?? 'NA';
    };

    const baseLog = {

      centreName: child.centreName ,
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode,
      currentUnit: child.selectUnit || 'NA',
      dob: child.dob,
      gender: child.gender,
      hospital: child.hospital,
      isUnique: true,
      type:admissionType,
      centreCode: child.centreCode ,
      bloodGroup: child.bloodGroup || 'NA',
      diagnosis: child.diagnosisSelected,
      city: child.city || "NA",
      state: child.state,
      age: getAge(child.dob),
      guardian1Name: guardianName(0),
      guardian2Name: guardianName(1),
      guardian1Occupation: guardianField(0, 'occupation'),
      guardian2Occupation: guardianField(1, 'occupation'),
      guardian1Relation: guardianField(0, 'relation'),
      guardian2Relation: guardianField(1, 'relation'),
      dateOfAdmission:child.dateOfAdmission,
      createdAt: new Date(),
      admissionCount:0 // it will be added later on
    };
   if(admissionCounterSnapshot?.exists) {

     const currentTotal = admissionCounterSnapshot!.data()!.totalLogs ?? 0;
     const counter = currentTotal + 1;
     baseLog['admissionCount'] = counter;
     const counterRef =this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').ref
     batch.set(counterRef, {
             totalLogs: counter,
             lastUpdated: new Date()
     })
     const admissionIdRef = this.mFirestore.collection(this.env.admissionLogHistory).doc().ref
     batch.set(admissionIdRef, {
       ...baseLog,
       admissionId: admissionIdRef.id,
       createdBy: createdBy,
     })



   }
    console.log('Trying to add admission log for:', child.childCode);

  return await batch.commit();


  }

  async createFollowupAdmissionChildLogObject(child: any, admissionType: string,createdBy:string='System'): Promise<void> {
    let admissionCounterSnapshot  = await  this.getAdmissionCounter()
    const batch = this.mFirestore.firestore.batch();
    const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
    const guardianName = (idx: number) => {
      const g = getGuardian(idx) as any;
      if (!g) return 'NA';
      const name = `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim();
      return name || 'NA';
    };
    const guardianField = (idx: number, field: string) => {
      const g = getGuardian(idx) as any;
      return g?.[field] ?? 'NA';
    };

    const baseLog = {
      centreName: child.centreName ,
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode,
      currentUnit: child.selectUnit || 'NA',
      dob: child.dob.toDate(),
      gender: child.gender,
      hospital: child.hospital,
      isUnique: false,
      type:admissionType,
      centreCode: child.centreCode ,
      bloodGroup: child.bloodGroup || 'NA',
      diagnosis: child.diagnosisSelected,
      city: child.city || "NA",
      state: child.state,
      age: getAge(child.dob),
      guardian1Name: guardianName(0),
      guardian2Name: guardianName(1),
      guardian1Occupation: guardianField(0, 'occupation'),
      guardian2Occupation: guardianField(1, 'occupation'),
      guardian1Relation: guardianField(0, 'relation'),
      guardian2Relation: guardianField(1, 'relation'),
      dateOfAdmission:child.dateOfAdmission,
      createdAt: new Date(),
      admissionCount:0 // it will be added later on
    };
    if(admissionCounterSnapshot?.exists) {

      const currentTotal = admissionCounterSnapshot!.data()!.totalLogs ?? 0;
      const counter = currentTotal + 1;
      baseLog['admissionCount'] = counter;
      const counterRef =this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').ref
      batch.set(counterRef, {
        totalLogs: counter,
        lastUpdated: new Date()
      })
      const admissionIdRef = this.mFirestore.collection(this.env.admissionLogHistory).doc().ref
      batch.set(admissionIdRef, {
        ...baseLog,
        admissionId: admissionIdRef.id,
        createdBy: createdBy,
      })

      console.log('Single Admission Log added for childCode:', child.childCode);

    }

    return await batch.commit();


  }
  async createDischargeAdmissionChildLogObject(child: any, admissionType: string,createdBy:string='System'): Promise<void> {
    let admissionCounterSnapshot  = await  this.getAdmissionCounter()
    const batch = this.mFirestore.firestore.batch();
    const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
    const guardianName = (idx: number) => {
      const g = getGuardian(idx) as any;
      if (!g) return 'NA';
      const name = `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim();
      return name || 'NA';
    };
    const guardianField = (idx: number, field: string) => {
      const g = getGuardian(idx) as any;
      return g?.[field] ?? 'NA';
    };

    const baseLog = {
      centreName: child.centreName,
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode,
      currentUnit: child.selectUnit || 'NA',
      dob: child.dob.toDate(),
      gender: child.gender,
      hospital: child.hospital,
      isUnique: true,
      type:admissionType,
      centreCode: child.centreCode ,
      bloodGroup: child.bloodGroup || 'NA',
      diagnosis: child.diagnosisSelected,
      city: child.city || "NA",
      state: child.state,
      age: getAge(child.dob),
      guardian1Name: guardianName(0),
      guardian2Name: guardianName(1),
      guardian1Occupation: guardianField(0, 'occupation'),
      guardian2Occupation: guardianField(1, 'occupation'),
      guardian1Relation: guardianField(0, 'relation'),
      guardian2Relation: guardianField(1, 'relation'),
      dateOfDischarge:child.dateOfDischarge,
      createdAt: new Date(),
      admissionCount:0 // it will be added later on
    };
    if(admissionCounterSnapshot?.exists) {

      const currentTotal = admissionCounterSnapshot!.data()!.totalLogs ?? 0;
      const counter = currentTotal + 1;
      baseLog['admissionCount'] = counter;
      const counterRef =this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').ref
      batch.set(counterRef, {
        totalLogs: counter,
        lastUpdated: new Date()
      })
      const admissionIdRef = this.mFirestore.collection(this.env.admissionLogHistory).doc().ref
      batch.set(admissionIdRef, {
        ...baseLog,
        admissionId: admissionIdRef.id,
        createdBy: createdBy,
      })

      console.log('Single Admission Log added for childCode:', child.childCode);

    }

    return await batch.commit();


  }

  async createFollowupDischargeAdmissionChildLogObject(child: any, admissionType: string,createdBy:string='System'): Promise<void> {
    let admissionCounterSnapshot  = await  this.getAdmissionCounter()
    const batch = this.mFirestore.firestore.batch();
    const getGuardian = (idx: number) => child.guardians?.[idx] ?? null;
    const guardianName = (idx: number) => {
      const g = getGuardian(idx) as any;
      if (!g) return 'NA';
      const name = `${g.firstName ?? ''} ${g.lastName ?? ''}`.trim();
      return name || 'NA';
    };
    const guardianField = (idx: number, field: string) => {
      const g = getGuardian(idx) as any;
      return g?.[field] ?? 'NA';
    };

    const baseLog = {
      centreName: child.centreName ,
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode,
      currentUnit: child.selectUnit || 'NA',
      dob: child.dob.toDate(),
      gender: child.gender,
      hospital: child.hospital,
      isUnique: false,
      type:admissionType,
      centreCode: child.centreCode ,
      bloodGroup: child.bloodGroup || 'NA',
      diagnosis: child.diagnosisSelected,
      city: child.city || "NA",
      state: child.state,
      age: getAge(child.dob),
      guardian1Name: guardianName(0),
      guardian2Name: guardianName(1),
      guardian1Occupation: guardianField(0, 'occupation'),
      guardian2Occupation: guardianField(1, 'occupation'),
      guardian1Relation: guardianField(0, 'relation'),
      guardian2Relation: guardianField(1, 'relation'),
      dateOfDischarge:child.dateOfDischarge,
      createdAt: new Date(),
      admissionCount:0 // it will be added later on
    };
    if(admissionCounterSnapshot?.exists) {

      const currentTotal = admissionCounterSnapshot!.data()!.totalLogs ?? 0;
      const counter = currentTotal + 1;
      baseLog['admissionCount'] = counter;
      const counterRef =this.mFirestore.collection(this.env.dashboard).doc('admissionCounts').ref
      batch.set(counterRef, {
        totalLogs: counter,
        lastUpdated: new Date()
      })
      const admissionIdRef = this.mFirestore.collection(this.env.admissionLogHistory).doc().ref
      batch.set(admissionIdRef, {
        ...baseLog,
        admissionId: admissionIdRef.id,
        createdBy: createdBy,
      })

      console.log('Single Admission Log added for childCode:', child.childCode);

    }

    return await batch.commit();


  }

  async getCurrentAdmissionsDuringDate(start: Timestamp, end: Timestamp): Promise<any[]> {
    /**
     * Return ALL admission log entries that occurred during the interval [start, end].
     *
     * IMPORTANT: If a child is admitted and discharged multiple times during the range,
     * EACH admission event is counted separately (not just unique children).
     *
     * Types of admissions counted:
     *  1. NEW ADMISSIONS: All admissions that occurred during [start, end]
     *     - dateOfAdmission >= start AND dateOfAdmission <= end
     *     - Each admission event is counted separately (same child can have multiple)
     *
     *  2. CARRY-FORWARD ADMISSIONS: Children admitted before start and still present
     *     - dateOfAdmission < start
     *     - AND not discharged before start (either no discharge OR discharged on/after start)
     *     - These children "carry forward" into the date range from previous period
     *     - Only counted ONCE per child (the most recent admission before start)
     *
     * Example:
     *  Child A: Admitted Jan 5, Discharged Jan 10, Re-admitted Jan 15, Discharged Jan 20
     *  Date Range: Jan 1 - Jan 31
     *  Result: 2 admission records (one for Jan 5, one for Jan 15)
     */

    if (!start || !end) return [];

    try {
      const startTime = this.getTimestamp(start);
      const endTime = this.getTimestamp(end);

      // Fetch all admissions that happened on or before 'end'
      const admissionsSnap = await this.mFirestore
        .collection(this.env.admissionLogHistory, ref => ref.where('dateOfAdmission', '<=', end))
        .get()
        .toPromise();

      // Fetch all discharges that happened before 'start'
      const dischargesBeforeStartSnap = await this.mFirestore
        .collection(this.env.admissionLogHistory, ref => ref.where('dateOfDischarge', '<', start))
        .get()
        .toPromise();

      // Group admissions by childCode
      const admissionsByChild = new Map<string, any[]>();
      admissionsSnap?.forEach(doc => {
        const data = doc.data() as any;
        const childCode = data?.childCode ?? doc.id;
        if (!admissionsByChild.has(childCode)) {
          admissionsByChild.set(childCode, []);
        }
        admissionsByChild.get(childCode)!.push({ id: doc.id, ...data });
      });

      // Group discharges before start by childCode
      const dischargesBeforeStart = new Map<string, any[]>();
      dischargesBeforeStartSnap?.forEach(doc => {
        const data = doc.data() as any;
        const childCode = data?.childCode ?? doc.id;
        if (!dischargesBeforeStart.has(childCode)) {
          dischargesBeforeStart.set(childCode, []);
        }
        dischargesBeforeStart.get(childCode)!.push({ id: doc.id, ...data });
      });

      const results: any[] = [];
      let carryForwardCount = 0;
      let newAdmissionCount = 0;

      // For each child, find ALL admission events during the range
      admissionsByChild.forEach((admissions, childCode) => {
        // Sort admissions by date (oldest first)
        const sortedAdmissions = admissions.slice().sort((a, b) => {
          const aDate = this.getTimestamp(a.dateOfAdmission);
          const bDate = this.getTimestamp(b.dateOfAdmission);
          return aDate - bDate;
        });

        const childDischargesBeforeStart = dischargesBeforeStart.get(childCode) || [];

        // Find ALL admissions that occurred during [start, end]
        const admissionsInRange = sortedAdmissions.filter(adm => {
          const admDate = this.getTimestamp(adm.dateOfAdmission);
          return admDate >= startTime && admDate <= endTime;
        });

        // Add ALL admissions that occurred during the range
        // (each admission event is counted separately, even for the same child)
        admissionsInRange.forEach(admission => {
          results.push({
            ...admission,
            isCarryForward: false,
            admissionType: 'New Admission'
          });
          newAdmissionCount++;
        });

        // Check if child was already admitted before start and not discharged before start
        // (carry-forward is counted only once per child - the most recent admission before start)
        if (admissionsInRange.length === 0) {
          const admissionCount = sortedAdmissions.length;
          const dischargeBeforeStartCount = childDischargesBeforeStart.length;
          const isCarryForward = admissionCount > dischargeBeforeStartCount;

          if (isCarryForward) {
            // CARRY-FORWARD ADMISSION: Use the most recent admission before start
            const representativeAdmission = {
              ...sortedAdmissions[sortedAdmissions.length - 1],
              isCarryForward: true,
              admissionType: 'Carry Forward'
            };
            results.push(representativeAdmission);
            carryForwardCount++;
          }
        }
      });

      console.log(`Found ${results.length} admission events during the date range:`);
      console.log(`- New admissions: ${newAdmissionCount} (includes multiple admissions for same child)`);
      console.log(`- Carry-forward admissions: ${carryForwardCount} (unique children)`);

      return results;

    } catch (error) {
      console.error('Error fetching admissions during date range:', error);
      return [];
    }
  }

  async getCurrentDischargesDuringDate(start: Timestamp, end: Timestamp): Promise<any[]> {
    /**
     * Return ALL discharge log entries that occurred during the interval [start, end].
     *
     * IMPORTANT: If a child is discharged multiple times during the range,
     * EACH discharge event is counted separately (not just unique children).
     *
     * Example:
     *  Child A: Admitted Jan 5, Discharged Jan 10, Re-admitted Jan 15, Discharged Jan 20
     *  Date Range: Jan 1 - Jan 31
     *  Result: 2 discharge records (one for Jan 10, one for Jan 20)
     */

    if (!start || !end) return [];

    try {
      const startTime = this.getTimestamp(start);
      const endTime = this.getTimestamp(end);

      // Fetch all discharge logs that happened during [start, end]
      const dischargesSnap = await this.mFirestore
        .collection(this.env.admissionLogHistory, ref =>
          ref.where('dateOfDischarge', '>=', start)
             .where('dateOfDischarge', '<=', end)
        )
        .get()
        .toPromise();

      const results: any[] = [];

      dischargesSnap?.forEach(doc => {
        const data = doc.data() as any;
        results.push({
          id: doc.id,
          ...data,
          dischargeType: data.type || 'Discharge'
        });
      });

      console.log(`Found ${results.length} discharge events during the date range`);

      return results;

    } catch (error) {
      console.error('Error fetching discharges during date range:', error);
      return [];
    }
  }

  /**
   * Get admission and discharge counts for a date range
   * Returns counts of all events (including multiple events for same child)
   */
  async getAdmissionDischargeCountsDuringDate(start: Timestamp, end: Timestamp): Promise<{
    admissions: number;
    newAdmissions: number;
    carryForwardAdmissions: number;
    discharges: number;
    admissionRecords: any[];
    dischargeRecords: any[];
  }> {
    try {
      const admissionRecords = await this.getCurrentAdmissionsDuringDate(start, end);
      const dischargeRecords = await this.getCurrentDischargesDuringDate(start, end);

      const newAdmissions = admissionRecords.filter(a => !a.isCarryForward).length;
      const carryForwardAdmissions = admissionRecords.filter(a => a.isCarryForward).length;

      return {
        admissions: admissionRecords.length,
        newAdmissions,
        carryForwardAdmissions,
        discharges: dischargeRecords.length,
        admissionRecords,
        dischargeRecords
      };
    } catch (error) {
      console.error('Error getting admission/discharge counts:', error);
      return {
        admissions: 0,
        newAdmissions: 0,
        carryForwardAdmissions: 0,
        discharges: 0,
        admissionRecords: [],
        dischargeRecords: []
      };
    }
  }

  /**
   * Helper method to convert various timestamp formats to milliseconds
   */
  private getTimestamp(value: any): number {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (value.toDate && typeof value.toDate === 'function') {
      return value.toDate().getTime();
    }
    if (value instanceof Date) {
      return value.getTime();
    }
    if (typeof value === 'string') {
      return new Date(value).getTime();
    }
    return 0;
  }
}
// TypeScript
/**
 * Calculate age in full years from a Firestore Timestamp, Date, or ISO string.
 * Returns 0 for missing/invalid/future dates.
 */
export function getAge(dob: Date | string | { toDate?: () => Date } | null | undefined): number {
  if (!dob) return 0;

  let birthDate: Date;
  if (typeof dob === 'object' && dob !== null && typeof (dob as any).toDate === 'function') {
    birthDate = (dob as any).toDate();
  } else if (dob instanceof Date) {
    birthDate = dob;
  } else {
    birthDate = new Date(dob as string);
  }

  if (isNaN(birthDate.getTime())) return 0;

  const today = new Date();
  if (birthDate > today) return 0;

  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return Math.max(0, age);
}

/**
 * Remove all keys with `undefined` values from an object (shallow).
 * Firestore's WriteBatch.set() throws if any field value is `undefined`.
 * `null` is accepted and preserved (it explicitly clears a field).
 */
export function stripUndefined<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T;
}

/*
Usage (in `src/app/services/admission-log.service.ts` inside getAdmissionLogsFromChild):

// get Date from Firestore Timestamp safely
const dobDate = (child.dob && (child.dob as any).toDate) ? (child.dob as any).toDate() : new Date(child.dob);

// include both raw dob Date and computed age in the log
dob: dobDate,
age: getAge(child.dob),
*/
